/**
 * TeamTab.tsx — Home tabs H-4: Team, read-only (spec §5; teamView.ts holds
 * every rule). Four KPI cards, the reps table with the stacked stage bars and
 * the rep flags, Rep detail under it, and the ✦ insight rail. No lead routing
 * and no Assign (deferred until after R4) — nothing here promises them.
 */
import { useState } from 'react'
import { stageDotFor } from '../../stageDotFor'
import { initials, stageDisplay, dismissedLine } from './homeView'
import { type TeamModel, type TeamInsight, type RepRow, type FlagTone, wonByRepLine, nothingLoggedLine, dealNote, repSummary } from './teamView'
import { SalesMetric } from './SalesDashboard'
import { accent, success, warning, danger } from '../../../../theme/tokens'

type Fmt = (pence: number, currency?: string) => string

const TONE: Record<FlagTone, string> = { warn: warning.DEFAULT, good: success.DEFAULT, accent: accent.DEFAULT }

function StageBar({ row, scale }: { row: RepRow; scale: number }) {
  const width = scale > 0 ? (row.openPence / scale) * 100 : 0
  return (
    <span className="k-team-bar" aria-hidden="true">
      <span className="k-team-bar-fill" style={{ width: `${width}%` }}>
        {row.stages.map(s => (
          <span key={s.stage} style={{ flexGrow: s.pence, background: stageDotFor(s.stage) }} />
        ))}
      </span>
    </span>
  )
}

export function TeamTab({ model, insights, quarter, fmt, capNote, historyRead, historyNote, onSelectDeal, onNavigate, onOpenPipeline }: {
  model: TeamModel
  insights: TeamInsight[]
  quarter: string | null
  fmt: Fmt
  /** "From the newest 200 deals only." when the deals list came back at the cap. */
  capNote: string | null
  historyRead: boolean
  /** The history's own cap sentence, when it came back at the cap. */
  historyNote: string | null
  onSelectDeal?: (id: string) => void
  onNavigate: (page: string) => void
  /** Opens the board filtered to an owner — the board's own Owner chip value
   *  ('—' selects the unassigned deals). */
  onOpenPipeline: (owner: string) => void
}) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const m = model
  const selected = m.rows.find(r => r.key === selectedKey) ?? m.rows[0] ?? null
  const scale = Math.max(0, ...m.rows.map(r => r.openPence))
  const legend = [...new Set(m.rows.flatMap(r => r.stages.map(s => s.stage)))]
  const q = quarter ?? 'the quarter'
  const n = m.nothingLogged
  const wonLine = wonByRepLine(m, fmt)

  const pipelineNote = [`${m.reps} ${m.reps === 1 ? 'rep' : 'reps'} · ${m.openCount} open ${m.openCount === 1 ? 'deal' : 'deals'}${m.weightedPence != null ? ` · ${fmt(m.weightedPence)} weighted` : ''}`, capNote].filter((x): x is string => !!x)

  return (
    <div className="k-myday-grid">
      <div className="flex flex-col gap-5 min-w-0">
        <div className="grid grid-cols-4 gap-5">
          <SalesMetric label="Team pipeline" value={fmt(m.openPence)} note={pipelineNote} />
          <SalesMetric label={`Won in ${q}`} value={m.wonPence != null ? fmt(m.wonPence) : '—'}
            note={m.wonPence == null ? 'Forecast not available' : wonLine ?? `Nothing won in ${q} yet`} />
          <div className={`k-metric${historyRead && m.week === 0 ? ' k-team-zero' : ''}`}>
            <span className="k-metric-label uppercase">Logged this week</span>
            <div className="flex flex-col gap-[7px]">
              <span className="k-metric-value" style={historyRead && m.week === 0 ? { color: danger.DEFAULT } : undefined}>{historyRead ? m.week : '—'}</span>
              <div className="k-metric-note">{historyRead ? `Last week: ${m.lastWeek} completed` : "The activity history couldn't be read."}</div>
              {historyNote && <div className="k-metric-note">{historyNote}</div>}
            </div>
          </div>
          <div className="k-metric">
            <span className="k-metric-label uppercase">Deals with nothing logged</span>
            <div className="flex flex-col gap-[7px]">
              <span className="k-metric-value" style={n.count > 0 ? { color: warning.DEFAULT } : undefined}>{n.assessed === 0 ? '—' : `${n.count} of ${n.of}`}</span>
              <div className="k-metric-note">{n.assessed === 0 ? 'Deal health not available' : nothingLoggedLine(n)}</div>
              {n.dismissed > 0 && <div className="k-metric-note">{dismissedLine(n.dismissed)}</div>}
            </div>
          </div>
        </div>

        <section className="k-card k-home-card" aria-label="Reps">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="k-home-card-title">Reps</h2>
            <div className="flex items-center gap-3 text-12 text-ink-secondary" aria-label="Stage colours">
              {legend.map(s => (
                <span key={s} className="flex items-center gap-[6px]"><span className="w-2 h-2 rounded-full" style={{ background: stageDotFor(s) }} aria-hidden="true" />{stageDisplay(s)}</span>
              ))}
            </div>
          </div>
          {m.rows.length === 0 ? (
            <p className="k-myday-quiet">No rep has a deal or a quota yet.</p>
          ) : (
            <div role="table" aria-label="Reps" className="flex flex-col">
              <div role="row" className="k-team-grid k-home-deal-head">
                <span role="columnheader">Rep</span><span role="columnheader">Open pipeline by stage</span>
                <span role="columnheader" className="text-right">Weighted</span><span role="columnheader" className="text-right">Won in {q}</span>
                <span role="columnheader" className="text-right">This week</span><span role="columnheader">✦ Flag</span>
              </div>
              {m.rows.map(r => {
                const pick = () => setSelectedKey(r.key)
                return (
                  <div key={r.key} role="row" tabIndex={0} aria-selected={selected?.key === r.key} onClick={pick}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick() } }}
                    className={`k-team-grid k-team-row cursor-pointer focus:outline-none${selected?.key === r.key ? ' is-selected' : ''}`}>
                    <span role="cell" className="flex items-center gap-3 min-w-0">
                      <span className="k-home-avatar" aria-hidden="true">{r.employeeId ? initials(r.name) : '—'}</span>
                      <span className="flex flex-col min-w-0">
                        <span className="text-14 font-semibold text-ink-primary truncate">{r.name}</span>
                        <span className="text-12 text-ink-secondary">{r.openDeals.length} open {r.openDeals.length === 1 ? 'deal' : 'deals'}</span>
                      </span>
                    </span>
                    <span role="cell" className="flex items-center gap-[10px] min-w-0">
                      <StageBar row={r} scale={scale} />
                      <span className="text-13 font-semibold text-ink-primary k-team-num">{fmt(r.openPence)}</span>
                    </span>
                    <span role="cell" className="text-right text-13 text-ink-body">{r.weightedPence != null ? fmt(r.weightedPence) : '—'}</span>
                    <span role="cell" className="text-right text-13 text-ink-body">{r.wonPence != null ? fmt(r.wonPence) : '—'}</span>
                    <span role="cell" className="text-right text-13" style={r.employeeId && historyRead && r.week === 0 ? { color: danger.DEFAULT } : undefined}>{r.employeeId && historyRead ? r.week : '—'}</span>
                    <span role="cell" className="text-13 min-w-0" style={r.flag ? { color: TONE[r.flag.tone] } : undefined}>{r.flag?.text ?? ''}</span>
                  </div>
                )
              })}
            </div>
          )}
          <span className="k-team-footer">
            Click a rep to see their deals.{m.unassignedWonPence > 0 ? ` ${fmt(m.unassignedWonPence)} won in ${q} has no rep assigned.` : ''}{capNote ? ` ${capNote}` : ''}
          </span>
        </section>

        {selected && (
          <section className="k-card k-home-card" aria-label="Rep detail">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="k-home-avatar" aria-hidden="true">{selected.employeeId ? initials(selected.name) : '—'}</span>
                <div className="flex flex-col gap-[2px] min-w-0">
                  <h2 className="k-home-card-title">{selected.name}</h2>
                  <span className="k-home-card-sub">{repSummary(selected, quarter, fmt)}</span>
                </div>
              </div>
              <button type="button" className="k-home-link" onClick={() => onOpenPipeline(selected.employeeId ? selected.name : '—')}>Open their pipeline ›</button>
            </div>
            {selected.openDeals.length === 0 ? (
              <p className="k-myday-quiet">No open deals.</p>
            ) : (
              <div role="list" aria-label={`${selected.name}'s open deals`} className="flex flex-col">
                {[...selected.openDeals].sort((a, b) => (b.basePence ?? 0) - (a.basePence ?? 0) || a.name.localeCompare(b.name)).map(d => {
                  const note = dealNote(d)
                  return (
                    <div key={d.id} role="listitem" className="k-team-deal">
                      <button type="button" className="text-14 font-semibold text-ink-primary truncate text-left bg-transparent border-0 p-0 cursor-pointer hover:underline" onClick={() => onSelectDeal?.(d.id)}>{d.name}</button>
                      <span className="flex items-center gap-[6px] text-13 text-ink-secondary"><span className="w-2 h-2 rounded-full" style={{ background: stageDotFor(d.stage) }} aria-hidden="true" />{stageDisplay(d.stage)}</span>
                      <span className="text-13 font-semibold text-ink-primary text-right">{fmt(d.amount, d.currency)}</span>
                      <span className="text-13" style={note ? { color: warning.DEFAULT } : undefined}>{note ?? ''}</span>
                    </div>
                  )
                })}
              </div>
            )}
          </section>
        )}
      </div>

      <div className="flex flex-col gap-5 min-w-0">
        {insights.length === 0 ? (
          <section className="k-card k-home-card" aria-label="Insights">
            <h2 className="k-home-card-title">✦ Insights</h2>
            <p className="k-myday-quiet">Nothing stands out across the team.</p>
          </section>
        ) : insights.map(i => (
          <section key={i.key} className="k-card k-home-card" aria-label={i.caption.replace('✦ ', '')}>
            <span className="k-team-caption">{i.caption}</span>
            <h2 className="k-team-insight-title">{i.title}</h2>
            <p className="k-myday-quiet">{i.body}</p>
            {i.action && (
              <button type="button" className="k-home-link self-start"
                onClick={() => (i.action!.kind === 'deal' && i.action!.dealId ? onSelectDeal?.(i.action!.dealId) : onNavigate('sales.activities'))}>
                {i.action.label}
              </button>
            )}
          </section>
        ))}
      </div>
    </div>
  )
}
